"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import React, { Component, createRef } from "react";
import Button from "@ingka/button";
import ChevronRight from "@ingka/ssr-icon/paths/chevron-right";
import ChevronLeft from "@ingka/ssr-icon/paths/chevron-left";
import ScrollIndicator from "./ScrollIndicator.mjs";
import smoothScroll from "@ingka/carousel-shared/utils/smoothScroll";
import SkipContent from "@ingka/skip-content";
import bezier from "bezier-easing";
import debounce from "@ingka/carousel-shared/utils/debounce";
import cx from "classnames";
class Carousel extends Component {
  constructor(props) {
    super(props);
    this.slideRefs = [];
    this.scroller = null;
    this.scrollIndicator = null;
    // Helper to get the indexes of the currently visible slides.
    this.slideToIndex = (slide) => this.slideRefs.indexOf(slide);
    this.state = {
      isScrolling: false,
      currentIndex: props.slideIndex || 0,
      visibleSlides: [],
      partiallyVisibleSlides: [],
      targetScroll: 0,
      currentScroll: 0,
      initialized: false,
      draggable: false,
      prevPosition: 0,
      prevSliderPosition: 0,
      slideShowDragging: false,
      intersectionObserverDisabled: false
    };
    if (props.id) {
      this.skipButtonId = `${props.prefix}-carousel__${props.id}-skip-btn`;
      this.previousBtnId = `${props.prefix}-carousel__${props.id}-prev-btn`;
      this.nextBtnId = `${props.prefix}-carousel__${props.id}-next-btn`;
    }
    this._visibleSlides = [];
    this.visibleIndexes = [];
    this.partiallyVisibleIndexes = [];
    this._partiallyVisibleSlides = [];
    this._visibleSlidesLength = null;
    this.onKeyUp = this.onKeyUp.bind(this);
    this.observeSlides = this.observeSlides.bind(this);
    this.setScrollState = this.setScrollState.bind(this);
    this.onScrollIndicatorClick = this.onScrollIndicatorClick.bind(this);
    this.slideRefs = [];
    this.onButtonClick = this.onButtonClick.bind(this);
    this.handleScrollBarMouseMove = this.handleScrollBarMouseMove.bind(this);
    this.handleScrollBarMouseDown = this.handleScrollBarMouseDown.bind(this);
    this.handleScrollBarMouseUp = this.handleScrollBarMouseUp.bind(this);
    this.handleSwipe = this.handleSwipe.bind(this);
    if (createRef) {
      this.component = createRef();
      this.slider = createRef();
    }
  }
  /**
   * Helper to clamp a value.
   */
  clamp(num, min, max) {
    return num <= min ? min : num >= max ? max : num;
  }
  setScrollState() {
    this.setState({
      currentScroll: this.scroller.scrollLeft
    });
  }
  /**
   * IntersectionObserver callback to handle slides coming in
   * or going out of view. The visible slides are tracked
   * on a property, and the final state is set by a debounced
   * call to setState, so that we're not doing too much work.
   */
  observeSlides(entries, observer) {
    const validObserver = observer && observer.thresholds;
    entries.forEach((entry) => {
      if (validObserver && entry.intersectionRatio >= observer.thresholds[1]) {
        if (this._visibleSlides.indexOf(entry.target) === -1) {
          this._visibleSlides.push(entry.target);
        }
      } else {
        this._visibleSlides = this._visibleSlides.filter(
          (item) => item !== entry.target
        );
      }
      if (validObserver && entry.intersectionRatio >= observer.thresholds[0]) {
        if (this._partiallyVisibleSlides.indexOf(entry.target) === -1) {
          this._partiallyVisibleSlides.push(entry.target);
        }
      } else {
        this._partiallyVisibleSlides = this._partiallyVisibleSlides.filter(
          (item) => item !== entry.target
        );
      }
      this.partiallyVisibleIndexes = this._partiallyVisibleSlides.map(
        this.slideToIndex
      );
    });
    this.setState({
      partiallyVisibleSlides: [...this._partiallyVisibleSlides]
    });
    if (this.debouncedSetVisibilityState) {
      this.debouncedSetVisibilityState(this._visibleSlides);
    }
  }
  /**
   * Helper to get visible indexes
   */
  _getVisibleMinMax(indexes, minmax) {
    if (indexes === void 0 || indexes.length === 0) {
      return null;
    }
    return Math[minmax].apply(Math, indexes);
  }
  /**
   * Gets the highest index of the currently visible slides.
   * If there are no registered visible slides, return null instead.
   */
  get maxVisible() {
    return this._getVisibleMinMax(this.visibleIndexes, "max");
  }
  /**
   * Gets the highest index of the currently partially visible slides.
   * If there are no registered partially visible slides, return null instead.
   */
  get maxPartiallyVisible() {
    return this._getVisibleMinMax(this.partiallyVisibleIndexes, "max");
  }
  /**
   * Gets the lowest index of the currently visible slides.
   */
  get minVisible() {
    return this._getVisibleMinMax(this.visibleIndexes, "min");
  }
  /**
   * Gets the lowest index of the currently partially visible slides.
   * If there are no registered partially visible slides, return null instead.
   */
  get minPartiallyVisible() {
    return this._getVisibleMinMax(this.partiallyVisibleIndexes, "min");
  }
  /**
   * Gets the first slide, if refs are available.
   */
  get firstSlide() {
    return this.slideRefs.length > 0 ? this.slideRefs[0] : null;
  }
  /**
   * Getst the last slide, if refs are available.
   */
  get lastSlide() {
    return this.slideRefs.length > 0 ? this.slideRefs[this.lastSlideIndex] : null;
  }
  /**
   * Scrolled fully to the left or first item inside viewport
   */
  get isFirstSlideVisible() {
    debounce(() => {
      if (this.state.currentScroll === 0) {
        return true;
      }
    }, 100);
    if (this._visibleSlides.indexOf(this.firstSlide) > -1) {
      return true;
    }
    return false;
  }
  /**
   * Scrolled fully to the right or last item inside viewport
   */
  get isLastSlideVisible() {
    debounce(() => {
      if (this.state.currentScroll === this.maxScrollWidth) {
        return true;
      }
    }, 100);
    if (this._visibleSlides.indexOf(this.lastSlide) > -1) {
      return true;
    }
    return false;
  }
  /**
   * Checks if all slides are currently visible.
   */
  get allSlidesVisible() {
    return this.isFirstSlideVisible && this.isLastSlideVisible;
  }
  /**
   * Gets the last slide index
   */
  get lastSlideIndex() {
    return this.slideRefs.length - 1;
  }
  /**
   * Gets the index of the next non-visible slide.
   */
  get nextSlide() {
    if (this.maxVisible === null) {
      return this.maxPartiallyVisible;
    }
    return this.maxVisible + 1;
  }
  /**
   * Gets the index of the previous non-visible slide.
   */
  get prevSlide() {
    if (this.minVisible === null) {
      return this.minPartiallyVisible;
    }
    return this.minVisible - 1;
  }
  /**
   * Getter for the cached value of visible slides length.
   * Defaults to the actual value in state, with a min clamp of 1.
   */
  get visibleSlidesLength() {
    if (this._visibleSlidesLength) {
      return this._visibleSlidesLength;
    }
    return this.clamp(this.state.visibleSlides.length, 1, Infinity);
  }
  /**
   * Gets the available width of the scrolling element.
   */
  get scrollWidth() {
    if (!this.scroller) {
      return Infinity;
    }
    return this.scroller.scrollWidth;
  }
  /**
   * Gets the maximum scroll offset an item can have.
   */
  get maxScrollWidth() {
    if (!this.scroller) {
      return Infinity;
    }
    return this.scrollWidth - this.scroller.offsetWidth;
  }
  /**
   * Get the scroll offset of a previous element (left). Will scroll so that the target
   * is placed at the right hand side of the scrollable container.
   */
  getPreviousScroll(target) {
    return !this.props.isSlideShow && this.props.rtl ? target.offsetLeft : target.offsetLeft + target.getBoundingClientRect().width - this.scroller.offsetWidth;
  }
  /**
   * Get the scroll offset of the next element (right).
   */
  getNextScroll(target) {
    return !this.props.isSlideShow && this.props.rtl ? target.offsetLeft - this.scroller.offsetWidth + target.getBoundingClientRect().width : target.offsetLeft;
  }
  /**
   * Callback for when any of the prev/next buttons are clicked, with
   * direction (right/left) passed as a string, defaulting to left.
   */
  onButtonClick(direction) {
    if (this.state.isScrolling) {
      return;
    }
    const nextSlide = this.clamp(
      direction === "right" ? this.nextSlide : this.prevSlide,
      0,
      this.lastSlideIndex
    );
    this.goToIndex(nextSlide, direction);
    if (direction === "right" && this.props.onBtnRightClick) {
      this.props.onBtnRightClick();
    }
    if (direction === "left" && this.props.onBtnLeftClick) {
      this.props.onBtnLeftClick();
    }
  }
  /**
   * Set state for scrolling to a specified index of the scroller items.
   */
  goToIndex(index, direction = "left") {
    index = this.clamp(index, 0, this.lastSlideIndex);
    const nextSlideElement = this.slideRefs[index];
    if (!nextSlideElement) {
      this.scrollTo(0);
      return;
    }
    const newScroll = direction === "right" ? this.getNextScroll(nextSlideElement) : this.getPreviousScroll(nextSlideElement);
    let targetScroll = newScroll;
    if (index === 0) {
      targetScroll = 0;
    }
    this.scrollTo(targetScroll);
  }
  /**
   * Do an easing scroll value calculation.
   * Used as a callback passed to the smoothScroll helper.
   * Uses a small bezier-calculation library to be able to do
   * the same kind of transition as the CSS version in design specs.
   */
  easing(elapsed, offset, gap, duration) {
    const f = bezier(0.4, 0, 0.4, 1);
    const progress = elapsed / duration;
    return gap * f(progress) + offset;
  }
  /**
   * Scrolls smoothly to the specified position, unless user preference overrides.
   */
  scrollTo(targetScroll) {
    this.setState({ targetScroll });
    this.stopObservingSlides();
    if (!this.state.initialized || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.scroller.scrollLeft = targetScroll;
      this.startObservingSlides();
      if (!this.state.initialized) {
        this.setState({
          initialized: true
        });
      }
      return;
    }
    this.setState({ isScrolling: true });
    if (this.scroller === null) {
      return;
    }
    smoothScroll(
      this.scroller,
      "scrollLeft",
      targetScroll,
      this.easing,
      200,
      () => {
        this.setState({ isScrolling: false });
        this.startObservingSlides();
      }
    );
  }
  /**
   * Add observer for when slides go in / out of view.
   */
  startObservingSlides() {
    this.setState({ slideShowDragging: false });
    if ("IntersectionObserver" in window && "IntersectionObserverEntry" in window) {
      this.visibleSlideObserver = new IntersectionObserver(this.observeSlides, {
        root: this.scroller,
        threshold: [0.99, 1e-3],
        rootMargin: "1px"
      });
      this.slideRefs.forEach(
        (slide) => !!slide && this.visibleSlideObserver?.observe(slide)
      );
      this.setState({
        intersectionObserverDisabled: false
      });
    } else {
      this.setState({
        intersectionObserverDisabled: true
      });
    }
  }
  /**
   * Disconnect the observer.
   */
  stopObservingSlides() {
    if (this.visibleSlideObserver) {
      this.visibleSlideObserver.disconnect();
    }
  }
  /**
   * Handle onKeyUp event for carousel, for left- and right arrow key navigation.
   */
  onKeyUp(e) {
    let arrowKey;
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      switch (e.key) {
        case "ArrowLeft":
          arrowKey = this.props.rtl ? "right" : "left";
          break;
        case "ArrowRight":
          arrowKey = this.props.rtl ? "left" : "right";
          break;
        default:
          return;
      }
      this.onButtonClick(arrowKey);
    }
  }
  /**
   * Handler for Scroll Indicator click event.
   */
  onScrollIndicatorClick(e) {
    if (!this.scrollIndicator) {
      return;
    }
    const { children = [], isSlideShow } = this.props;
    const numberOfItems = children.length;
    if (e.target === this.scrollIndicator.childNodes[0]) {
      return;
    }
    const scrollIndicatorLeftOffset = this.scrollIndicator.getBoundingClientRect().left;
    const scrollIndicatorWidth = this.scrollIndicator.getBoundingClientRect().width;
    const clickedPointInPercentage = (e.clientX - scrollIndicatorLeftOffset) * 100 / scrollIndicatorWidth;
    const clickedPoint = clickedPointInPercentage * this.maxScrollWidth / 100;
    if (isSlideShow) {
      const scrollPointsDistance = this.maxScrollWidth / (numberOfItems - 1);
      const stepWidth = this.maxScrollWidth / numberOfItems;
      let newScrollPosition = Math.round(clickedPoint / scrollPointsDistance) * scrollPointsDistance;
      if (clickedPoint < stepWidth) {
        newScrollPosition = 0;
      } else if (clickedPoint > stepWidth * (numberOfItems - 1)) {
        newScrollPosition = this.maxScrollWidth;
      }
      this.scrollTo(
        this.props.rtl ? newScrollPosition - this.maxScrollWidth : newScrollPosition
      );
    } else {
      this.scrollTo(
        this.props.rtl ? clickedPoint - this.maxScrollWidth : clickedPoint
      );
    }
  }
  adjustSliderPosition() {
    this.goToIndex(this.state.currentIndex);
  }
  handleSwipe() {
    if (!this.state.slideShowDragging) return;
    requestAnimationFrame(() => {
      this.setState({
        slideShowDragging: false
      });
    });
  }
  handleScrollBarMouseDown(e) {
    this.setState({
      draggable: true,
      prevPosition: e.pageX,
      prevSliderPosition: this.scroller.scrollLeft,
      slideShowDragging: true
    });
  }
  handleScrollBarMouseUp() {
    if (!this.state.draggable) return;
    this.setState({
      draggable: false
    });
  }
  handleScrollBarMouseMove(e) {
    if (!this.state.draggable) return;
    const stepRatio = this.scrollWidth / this.scrollIndicator.scrollWidth;
    this.scroller.scrollLeft = this.state.prevSliderPosition + (e.pageX - this.state.prevPosition) * stepRatio;
  }
  componentDidMount() {
    this.resizeEventCallback = debounce(() => {
      this._visibleSlidesLength = this._visibleSlides ? this._visibleSlides.length : null;
      this.props.onResize && this.props.onResize();
      this.stopObservingSlides();
      this.startObservingSlides();
      if (!!this.slideRefs[0] && this.slideRefs[0].getBoundingClientRect().width * this.slideRefs.length <= this.scroller.offsetWidth) {
        this.adjustSliderPosition();
      }
    }, 250);
    window.addEventListener("mousemove", this.handleScrollBarMouseMove);
    window.addEventListener("mouseup", this.handleScrollBarMouseUp);
    window.addEventListener("resize", this.resizeEventCallback);
    this.scroller?.addEventListener("wheel", this.handleSwipe, {
      passive: true
    });
    this.scroller?.addEventListener("scroll", this.setScrollState);
    this.debouncedSetVisibilityState = debounce(
      (visibleSlides) => {
        if (!this._visibleSlidesLength) {
          this._visibleSlidesLength = visibleSlides.length;
        }
        this.setState({
          visibleSlides
          // This will never update as long as the array is the same instance...
        });
        const visibleIndexes = visibleSlides.map(this.slideToIndex);
        if (this.visibleIndexes.join(",") !== visibleIndexes.join(",")) {
          if (this.props.onVisibleSlideChange)
            this.props.onVisibleSlideChange([...visibleIndexes]);
        }
        this.visibleIndexes = visibleIndexes;
      },
      100
    );
    this.startObservingSlides();
    this.adjustSliderPosition();
  }
  componentWillUnmount() {
    window.removeEventListener("mousemove", this.handleScrollBarMouseMove);
    window.removeEventListener("mouseup", this.handleScrollBarMouseUp);
    if (this.resizeEventCallback) {
      window.removeEventListener("resize", this.resizeEventCallback);
    }
    this.scroller?.removeEventListener("wheel", this.handleSwipe);
    this.scroller?.removeEventListener("scroll", this.setScrollState);
    this.stopObservingSlides();
  }
  componentDidUpdate(prevProps) {
    if (this.props.slideIndex !== prevProps.slideIndex) {
      this.goToIndex(this.props.slideIndex);
    }
    if (prevProps.children.length !== this.props.children.length) {
      this.stopObservingSlides();
      this.startObservingSlides();
      this.slideRefs.length = 0;
      this.setScrollState();
    }
  }
  render() {
    const {
      id,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      slideIndex = 0,
      hasNoControls = false,
      children = [],
      prefix = "",
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      rtl,
      role = "list",
      ariaLabelLeftBtn = "See previous items",
      ariaLabelRightBtn = "See next items",
      controlBtnType = "primary",
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onBtnRightClick,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onBtnLeftClick,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onResize,
      itemClass,
      contentClass,
      bodyWrapperClass,
      isSlideShow,
      hasNoScrollbar,
      hasSlideCounter,
      className,
      skipButtonText = "Skip listing",
      counterText = "of",
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onVisibleSlideChange,
      ...otherProps
    } = this.props;
    const { currentScroll, isScrolling, slideShowDragging, draggable } = this.state;
    const isLastSlide = this.isLastSlideVisible;
    const isFirstSlide = this.isFirstSlideVisible;
    const isSingleSlide = this.allSlidesVisible;
    const scrollPercent = currentScroll / this.maxScrollWidth * 100;
    const scrollIndicatorWidth = this.scroller === null ? 0 : this.scroller.offsetWidth / this.scroller.scrollWidth * 100;
    const childrenAsArray = React.Children.toArray(children).filter(
      (c) => c
    );
    const slideCounter = (this.visibleIndexes[0] ?? this.partiallyVisibleIndexes[0]) + 1;
    const wrappedChildren = childrenAsArray.map((child, i) => {
      return /* @__PURE__ */ jsx(
        "div",
        {
          ref: (el) => this.slideRefs[i] = el,
          className: cx(`${prefix}carousel-slide`, itemClass),
          role: child.props?.role ?? (role === "list" ? "listitem" : void 0),
          children: role === "list" && child.props.role === "listitem" ? React.cloneElement(child, { role: void 0 }) : child
        },
        `${id}-${i}`
      );
    });
    const carouselClasses = cx(
      `${prefix}carousel`,
      className,
      isSlideShow && `${prefix}carousel__slide-show`,
      this.state.intersectionObserverDisabled && `${prefix}carousel--intersection-observer-disabled`,
      draggable && `${prefix}carousel--dragging`
    );
    const carouselContentClasses = cx(
      `${prefix}carousel__content`,
      contentClass,
      // scroll-snap clashes with programmatically scrolling
      !isScrolling && `${prefix}carousel__content--snap-slide`,
      slideShowDragging && `${prefix}carousel__content--drag-slide`,
      isSingleSlide && `${prefix}carousel__content--single-slide`
    );
    const carouselBodyClasses = cx(
      `${prefix}carousel__body`,
      isSlideShow && `${prefix}carousel__body--slide-show`
    );
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: carouselClasses,
        tabIndex: -1,
        ref: this.component,
        ...otherProps,
        onKeyUp: this.onKeyUp,
        children: /* @__PURE__ */ jsxs(
          SkipContent,
          {
            prefix,
            label: skipButtonText,
            targetId: this.skipButtonId,
            children: [
              /* @__PURE__ */ jsxs("div", { className: `${prefix}carousel__wrapper`, children: [
                !hasNoControls && /* @__PURE__ */ jsx(
                  Button,
                  {
                    id: this.previousBtnId,
                    "aria-controls": `${prefix}carousel__${id}`,
                    prefix,
                    size: "small",
                    iconOnly: true,
                    ssrIcon: ChevronLeft,
                    type: controlBtnType,
                    "aria-label": ariaLabelLeftBtn,
                    className: cx(
                      `${prefix}carousel__button`,
                      isFirstSlide && `${prefix}carousel__button--hidden`
                    ),
                    onClick: () => this.onButtonClick("left")
                  }
                ),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: carouselContentClasses,
                    ref: (scroller) => this.scroller = scroller,
                    children: /* @__PURE__ */ jsx(
                      "div",
                      {
                        className: cx(
                          `${prefix}carousel__body-wrapper`,
                          bodyWrapperClass
                        ),
                        children: /* @__PURE__ */ jsx(
                          "div",
                          {
                            id: `${prefix}carousel__${id}`,
                            className: carouselBodyClasses,
                            ref: this.slider,
                            role,
                            children: wrappedChildren
                          }
                        )
                      }
                    )
                  }
                ),
                !hasNoControls && /* @__PURE__ */ jsx(
                  Button,
                  {
                    id: this.nextBtnId,
                    "aria-controls": `${prefix}carousel__${id}`,
                    prefix,
                    size: "small",
                    iconOnly: true,
                    ssrIcon: ChevronRight,
                    type: controlBtnType,
                    "aria-label": ariaLabelRightBtn,
                    className: cx(
                      `${prefix}carousel__button`,
                      `${prefix}carousel__button--right`,
                      isLastSlide && `${prefix}carousel__button--hidden`
                    ),
                    onClick: () => this.onButtonClick("right")
                  }
                )
              ] }),
              !isSingleSlide && !hasNoScrollbar && /* @__PURE__ */ jsxs("div", { className: `${prefix}carousel__counter--wrapper`, children: [
                /* @__PURE__ */ jsx(
                  ScrollIndicator,
                  {
                    prefix,
                    barPosition: scrollPercent,
                    barWidth: scrollIndicatorWidth,
                    ref: (el) => this.scrollIndicator = el,
                    onClick: this.onScrollIndicatorClick,
                    onMouseDown: this.handleScrollBarMouseDown,
                    draggable: this.state.draggable
                  }
                ),
                hasSlideCounter && !!slideCounter && /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: `${prefix}carousel__counter`,
                    children: `${slideCounter} ${counterText} ${childrenAsArray.length}`
                  }
                )
              ] })
            ]
          }
        )
      }
    );
  }
}
var Carousel_default = Carousel;
export {
  Carousel_default as default
};
